// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  FolderLock, Plus, Trash2, Save, Users, FileBadge, BookOpen, Wrench, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PAYERS = 5;

// Ensure every row has a stable client-side id for React keys
// (server data has no per-row id and items are mutable).
const withUid = (arr) => (arr || []).map(x => x._uid ? x : { ...x, _uid: crypto.randomUUID() });

export default function PermanentApprovalsPage() {
  const { isAdmin } = useAuth();
  const isAdminUser = isAdmin();
  const area = localStorage.getItem('selectedArea') || 'congregacao';
  const areaIcon = area === 'congregacao' ? BookOpen : Wrench;
  const AreaIcon = areaIcon;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/permanent-approvals/${area}`);
      const enriched = {
        ...res.data,
        aprovados_pagamento: withUid(res.data.aprovados_pagamento),
        resolucoes_recorrentes: withUid(res.data.resolucoes_recorrentes),
      };
      setData(enriched);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar aprovações');
    } finally {
      setLoading(false);
    }
  }, [area]);

  useEffect(() => { load(); }, [load]);

  const set = (patch) => setData(prev => ({ ...prev, ...patch }));

  const addPayer = () => {
    const arr = data.aprovados_pagamento || [];
    if (arr.length >= MAX_PAYERS) {
      toast.warning(`Limite de ${MAX_PAYERS} irmãos aprovados (S-27 §5)`);
      return;
    }
    set({ aprovados_pagamento: [...arr, { _uid: crypto.randomUUID(), name: '', note: '' }] });
  };

  const updatePayer = (i, key, value) => {
    const arr = [...(data.aprovados_pagamento || [])];
    arr[i] = { ...arr[i], [key]: value };
    set({ aprovados_pagamento: arr });
  };

  const removePayer = (i) => {
    const arr = (data.aprovados_pagamento || []).filter((_, idx) => idx !== i);
    set({ aprovados_pagamento: arr });
  };

  const addResolution = () => {
    set({
      resolucoes_recorrentes: [
        ...(data.resolucoes_recorrentes || []),
        { _uid: crypto.randomUUID(), descricao: '', valor: null, frequencia: 'mensal', aprovado_em: '' },
      ],
    });
  };

  const updateResolution = (i, key, value) => {
    const arr = [...(data.resolucoes_recorrentes || [])];
    arr[i] = { ...arr[i], [key]: key === 'valor' ? (value === '' ? null : Number(value)) : value };
    set({ resolucoes_recorrentes: arr });
  };

  const removeResolution = (i) => {
    set({
      resolucoes_recorrentes: (data.resolucoes_recorrentes || []).filter((_, idx) => idx !== i),
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const strip = (arr) => (arr || []).map(({ _uid, ...rest }) => rest);
      const payload = {
        aprovados_pagamento: strip(data.aprovados_pagamento),
        previsao_gastos_valor: data.previsao_gastos_valor,
        previsao_gastos_atualizado_em: data.previsao_gastos_atualizado_em,
        limite_coordenador_valor: data.limite_coordenador_valor,
        resolucoes_recorrentes: strip(data.resolucoes_recorrentes),
        notas: data.notas,
      };
      const res = await axios.put(`${API}/permanent-approvals/${area}`, payload);
      setData({
        ...res.data,
        aprovados_pagamento: withUid(res.data.aprovados_pagamento),
        resolucoes_recorrentes: withUid(res.data.resolucoes_recorrentes),
      });
      toast.success('Aprovações salvas');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return <Layout><div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="permanent-approvals-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground font-heading flex items-center gap-2">
              <FolderLock className="w-7 h-7 text-amber-600" />
              Aprovações Permanentes
            </h1>
            <p className="text-muted-foreground mt-1 flex items-center gap-2">
              <AreaIcon className="w-4 h-4" />
              {area === 'congregacao' ? 'Congregação' : 'Manutenção'} — conforme S-27 §58
            </p>
          </div>
          <Badge variant="outline">
            Atualizado em {new Date(data.updated_at).toLocaleString('pt-BR')}
          </Badge>
        </div>

        {!isAdminUser && (
          <Card className="border-amber-500/40 bg-amber-50/30 dark:bg-amber-950/20">
            <CardContent className="p-4 text-sm">
              Você está em modo somente leitura. Apenas administradores podem editar.
            </CardContent>
          </Card>
        )}

        {/* Approved payers — §5 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Irmãos aprovados para pagamentos
              <Badge variant="outline" data-testid="payers-count">
                {(data.aprovados_pagamento || []).length}/{MAX_PAYERS}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              S-27 §5: até {MAX_PAYERS} irmãos aprovados pelo COC para fazer pagamentos eletrônicos ou por cheque.
            </p>
            {(data.aprovados_pagamento || []).map((p, i) => (
              <div key={p._uid || `payer-${i}`} className="flex gap-2 items-start" data-testid={`payer-row-${i}`}>
                <Input
                  placeholder="Nome do irmão aprovado"
                  value={p.name || ''}
                  onChange={(e) => updatePayer(i, 'name', e.target.value)}
                  disabled={!isAdminUser}
                  data-testid={`payer-name-${i}`}
                />
                <Input
                  placeholder="Observação (opcional)"
                  value={p.note || ''}
                  onChange={(e) => updatePayer(i, 'note', e.target.value)}
                  disabled={!isAdminUser}
                />
                {isAdminUser && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removePayer(i)}
                    className="text-red-600"
                    data-testid={`remove-payer-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            {isAdminUser && (
              <Button variant="outline" size="sm" onClick={addPayer} data-testid="add-payer-button">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar irmão
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Reserve / limit values */}
        <div className="grid sm:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Previsão de gastos (§11)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label className="text-xs">Valor de reserva aprovado</Label>
              <Input
                type="number"
                step="0.01"
                value={data.previsao_gastos_valor ?? ''}
                onChange={(e) => set({ previsao_gastos_valor: e.target.value === '' ? null : Number(e.target.value) })}
                disabled={!isAdminUser}
                data-testid="previsao-gastos-input"
                placeholder="0,00"
              />
              <Label className="text-xs">Aprovado em</Label>
              <Input
                type="date"
                value={data.previsao_gastos_atualizado_em || ''}
                onChange={(e) => set({ previsao_gastos_atualizado_em: e.target.value })}
                disabled={!isAdminUser}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Limite do coordenador (§21)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label className="text-xs">Valor máximo por despesa não rotineira</Label>
              <Input
                type="number"
                step="0.01"
                value={data.limite_coordenador_valor ?? ''}
                onChange={(e) => set({ limite_coordenador_valor: e.target.value === '' ? null : Number(e.target.value) })}
                disabled={!isAdminUser}
                data-testid="limite-coordenador-input"
                placeholder="0,00"
              />
            </CardContent>
          </Card>
        </div>

        {/* Recurring resolutions — §23, §24 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileBadge className="w-5 h-5" />
              Resoluções recorrentes (§23, §24)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data.resolucoes_recorrentes || []).map((r, i) => (
              <div key={r._uid || `res-${i}`} className="grid sm:grid-cols-12 gap-2 border border-border rounded-xl p-3" data-testid={`resolution-row-${i}`}>
                <Input
                  className="sm:col-span-5"
                  placeholder="Descrição (ex: Construção mundial)"
                  value={r.descricao || ''}
                  onChange={(e) => updateResolution(i, 'descricao', e.target.value)}
                  disabled={!isAdminUser}
                />
                <Input
                  className="sm:col-span-2"
                  type="number"
                  step="0.01"
                  placeholder="Valor"
                  value={r.valor ?? ''}
                  onChange={(e) => updateResolution(i, 'valor', e.target.value)}
                  disabled={!isAdminUser}
                />
                <select
                  className="sm:col-span-2 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={r.frequencia || 'mensal'}
                  onChange={(e) => updateResolution(i, 'frequencia', e.target.value)}
                  disabled={!isAdminUser}
                  data-testid={`resolution-frequencia-${i}`}
                >
                  <option value="mensal">Mensal</option>
                  <option value="trimestral">Trimestral</option>
                  <option value="anual">Anual</option>
                </select>
                <Input
                  className="sm:col-span-2"
                  type="date"
                  value={r.aprovado_em || ''}
                  onChange={(e) => updateResolution(i, 'aprovado_em', e.target.value)}
                  disabled={!isAdminUser}
                />
                {isAdminUser && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeResolution(i)}
                    className="text-red-600 sm:col-span-1"
                    data-testid={`remove-resolution-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            {isAdminUser && (
              <Button variant="outline" size="sm" onClick={addResolution} data-testid="add-resolution-button">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar resolução
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Observações gerais</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              rows={3}
              value={data.notas || ''}
              onChange={(e) => set({ notas: e.target.value })}
              disabled={!isAdminUser}
              placeholder="Documentos anexados, TO-60, acordo com outras congregações, etc."
              data-testid="notes-textarea"
            />
          </CardContent>
        </Card>

        {isAdminUser && (
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="save-button">
              <Save className="w-4 h-4 mr-2" />
              Salvar
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
